// Vlastnosti souboru / složky / zástupce: Obecné · Sdílení (ACL jádra) · Verze.
import { registerComponent } from '@core/views/factory';
import { core, ApiError } from '@core/api';
import { h, clear } from '@core/dom';
import { t, tk, fmtDateTime, fmtDate, fmtSize } from '@core/i18n';
import { icon, fileIcon } from '@core/icons';
import { IwTabs } from '@core/components/misc';
import { toast } from '@core/components/toast';
import { confirm } from '@core/components/dialog';
import { events } from '@core/events';
import { shareEditor } from '@core/components/share';
import type { StorageItem, Shortcut } from '@core/contract';
import { mimeLabel } from '@core/mime';

interface VersionsPayload { current: number; versions: { version: number; size: number; mime: string | null; createdAt: string; createdBy: string | null; url: string }[]; canWrite: boolean }

registerComponent('desktop-properties', ({ desc }) => {
  const p = desc.payload as { item: StorageItem; path: { id: string; name: string }[]; shortcut?: Shortcut; owner?: string | null; createdBy?: string | null; updatedBy?: string | null; access?: string | null; tab?: string | null };
  const prefix = (JSON.parse(document.getElementById('iwan-boot')?.textContent ?? '{}') as { prefix?: string }).prefix ?? '';
  const it = p.item;
  const kind = it.isShortcut ? t('desktop.props.shortcut') : it.kind === 'folder' ? t('desktop.props.folder') : mimeLabel(it.mime, it.name); // „Dokument PDF“; přesný MIME o řádek níž
  const row = (label: string, value: string | Node) => h('div', { class: 'about-row' }, h('span', { class: 'muted' }, label), h('span', null, value));
  const accessLabel = p.access ? t('desktop.props.access_' + p.access) : '—';

  const general = h(
    'div',
    null,
    row(t('desktop.props.kind'), kind),
    it.kind === 'file' ? row(t('desktop.props.size'), fmtSize(it.size)) : null,
    it.mime ? row(t('desktop.props.mime'), it.mime) : null,
    row(t('desktop.props.location'), p.path.slice(0, -1).map((x) => (x.id === '__shared__' ? t('soubory.shared_me') : x.name)).join(' / ') || '/'),
    row(t('desktop.props.owner'), p.owner === 'shared' ? t('soubory.shared') : (p.owner ?? '—')),
    row(t('desktop.props.access'), accessLabel),
    row(t('desktop.props.created'), fmtDateTime(it.createdAt) + (p.createdBy ? ' · ' + p.createdBy : '')),
    it.updatedAt ? row(t('desktop.props.modified'), fmtDateTime(it.updatedAt) + (p.updatedBy ? ' · ' + p.updatedBy : '')) : null,
    p.shortcut ? row(t('desktop.props.target'), h('span', { class: 'mono' }, `${p.shortcut.app}/${p.shortcut.window} ${JSON.stringify(p.shortcut.params)}`)) : null,
  );

  // ---- sdílení (společný editor, API sharing.*)
  const shareBox = h('div');
  const loadShares = (): void => {
    clear(shareBox);
    shareBox.appendChild(shareEditor('storage', it.id, { inheritsHint: it.kind === 'folder' }));
  };

  // ---- verze
  const versionBox = h('div', { class: 'version-box' });
  async function loadVersions(): Promise<void> {
    clear(versionBox);
    versionBox.appendChild(h('div', { class: 'spinner' }));
    try {
      const r = await core<VersionsPayload>('storage', 'versions', { id: it.id });
      clear(versionBox);
      versionBox.appendChild(h('p', { class: 'muted small' }, t('core.version.current', { n: r.current })));
      if (!r.versions.length) {
        versionBox.appendChild(h('p', { class: 'muted' }, t('core.version.none')));
        return;
      }
      const tbody = h('tbody', null, r.versions.map((v) => {
        const dl = h('a', { class: 'btn ghost sm', href: prefix + v.url + '&dl=1', target: '_blank', rel: 'noopener', title: t('core.version.download') }, icon('download'));
        const restore = h('button', { type: 'button', class: 'btn ghost sm', title: t('core.version.restore'), disabled: !r.canWrite }, icon('rotate'));
        restore.addEventListener('click', async () => {
          if (!(await confirm(t('core.version.confirm_restore', { n: v.version })))) return;
          try {
            await core('storage', 'restoreVersion', { id: it.id, version: v.version });
            events.kick();
            toast(t('core.version.restored'), 'ok');
            void loadVersions();
          } catch (e) {
            toast(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'error');
          }
        });
        const del = h('button', { type: 'button', class: 'btn ghost sm', title: t('core.version.delete'), disabled: !r.canWrite }, icon('trash'));
        del.addEventListener('click', async () => {
          if (!(await confirm(t('core.version.confirm_delete', { n: v.version }), { danger: true }))) return;
          try {
            await core('storage', 'deleteVersion', { id: it.id, version: v.version });
            void loadVersions();
          } catch (e) {
            toast(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'error');
          }
        });
        return h('tr', null, h('td', null, String(v.version)), h('td', null, fmtDateTime(v.createdAt), v.createdBy ? h('span', { class: 'muted' }, ` ${t('core.version.by')} ${v.createdBy}`) : null), h('td', { class: 'right' }, fmtSize(v.size)), h('td', { class: 'right version-actions' }, dl, restore, del));
      }));
      versionBox.appendChild(h('table', { class: 't' }, h('thead', null, h('tr', null, h('th', null, '#'), h('th', null, t('desktop.props.modified')), h('th', { class: 'right' }, t('desktop.props.size')), h('th', null, ''))), tbody));
    } catch (e) {
      versionBox.replaceChildren(h('p', { class: 'muted' }, tk((e as ApiError).message, (e as ApiError).params ?? undefined)));
    }
  }

  const tabs = new IwTabs();
  const defs = [{ id: 'general', label: t('desktop.props.tab_general') }];
  if (it.space === 'user' && it.parentId) defs.push({ id: 'share', label: t('core.share.title') });
  if (it.kind === 'file' && !it.isShortcut) defs.push({ id: 'versions', label: t('core.version.title') });
  const loaded = new Set<string>();
  tabs.onChange = (id) => {
    if (loaded.has(id)) return;
    loaded.add(id);
    if (id === 'share') loadShares();
    if (id === 'versions') void loadVersions();
  };
  tabs.setTabs(defs);
  tabs.panel('general').appendChild(general);
  tabs.panel('share').appendChild(shareBox);
  tabs.panel('versions').appendChild(versionBox);
  if (p.tab && defs.some((d) => d.id === p.tab)) tabs.select(p.tab);

  const el = h(
    'div',
    { class: 'view props-view' },
    h('div', { class: 'props-head' }, icon(fileIcon(it.mime, it.isShortcut, it.kind === 'folder'), 'icon big'), h('h2', null, it.name)),
    tabs,
  );
  return { el };
});
